import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, Typography, Paper, IconButton, 
  Grid, Card, CardMedia, CardActions, Alert
} from '@mui/material';
import {
  CloudUploadOutlined as CloudUploadIcon,
  DeleteOutlined as DeleteIcon,
  PhotoCameraOutlined as PhotoCameraIcon,
  AddOutlined as AddIcon
} from '@mui/icons-material';

const EMPTY_PHOTOS = [];

const PhotoUpload = ({ onPhotosChange, maxPhotos = 5, maxFileSize = 5, existingPhotos }) => {
  const [photos, setPhotos] = useState(existingPhotos ?? EMPTY_PHOTOS);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Sync from parent only when existingPhotos is explicitly provided (controlled mode)
  useEffect(() => {
    if (existingPhotos !== undefined) {
      setPhotos(existingPhotos);
    }
  }, [existingPhotos]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    setError('');
    
    if (photos.length + files.length > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    const newPhotos = [];
    
    Array.from(files).forEach((file) => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload only image files');
        return;
      }

      // Check file size (MB)
      if (file.size / (1024 * 1024) > maxFileSize) {
        setError(`File size must be less than ${maxFileSize}MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newPhoto = {
          id: Date.now() + Math.random(),
          file: file,
          preview: e.target.result,
          name: file.name
        };
        
        newPhotos.push(newPhoto);
        
        if (newPhotos.length === files.length) {
          setPhotos((prev) => {
            const updated = [...prev, ...newPhotos];
            onPhotosChange(updated);
            return updated;
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removePhoto = (photoId) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Upload Photos
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Drag and drop images here, or click to select files
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Maximum {maxPhotos} photos, {maxFileSize}MB each
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<PhotoCameraIcon />}
          sx={{ mt: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            openFileDialog();
          }}
        >
          Select Photos
        </Button>
      </Paper>

      {photos.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Uploaded Photos ({photos.length}/{maxPhotos})
          </Typography>
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={photo.preview || (photo.url ? (photo.url.startsWith('http') ? photo.url : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${photo.url}`) : '')}
                    alt={photo.name || photo.filename}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                    <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                      {photo.name || photo.filename}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removePhoto(photo.id)}
                      aria-label="Remove photo"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {photos.length < maxPhotos && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openFileDialog}
          >
            Add More Photos
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PhotoUpload; 